import { Logger } from 'loglevel';
import { ENV } from '../config/env';

const apiBaseUrl = ENV.BACK_EDI_URL;

export interface ValidationResult {
  isValid: boolean;
  error: string | undefined;
}

export enum ValidateTiersOctaveResult {
  OK = 0,
  TiersNotFound = 1,
  ErrorInCreateUser = 2,
  ErrorInCreateClient = 3,
  Cancel = 4,
}

export interface EdiOctaveUser {
  DisplayName: string;
  CodeOctave: string;
  NumSerie: string;
  NumFacture: string;
}

export class EdiOctaveTiersService {
  constructor(
    private readonly getAccessToken: () => Promise<string>,
    private readonly logger: Logger,
  ) {}

  async tryFetchValidateTiersOctave(user: EdiOctaveUser): Promise<ValidationResult> {
    try {
      const response = await this.fetchValidateTiersOctave(user);
      const validationResult: unknown = await response.json();

      switch (validationResult) {
        case ValidateTiersOctaveResult.OK:
          return {
            isValid: true,
            error: undefined,
          };
        case ValidateTiersOctaveResult.TiersNotFound:
          return {
            isValid: false,
            error: 'Les informations renseignées ne correspondent à aucun compte Sage.',
          };
        case ValidateTiersOctaveResult.ErrorInCreateUser:
          return {
            isValid: false,
            error: "Une erreur est survenue lors de la création de l'utilisateur.",
          };
        case ValidateTiersOctaveResult.ErrorInCreateClient:
          return {
            isValid: false,
            error: 'Une erreur est survenue lors du référencement du compte.',
          };
        case ValidateTiersOctaveResult.Cancel:
          return {
            isValid: false,
            error: 'Le référencement a été annulé.',
          };
        default:
          return {
            isValid: false,
            error: 'Le service a retourné une réponse inattendue.',
          };
      }
    } catch (error) {
      this.logger.error('❌ Error fetching post validate user Octave:', error);
      return {
        isValid: false,
        error: (error as Error)?.message ?? 'An error occurred while validating the user.',
      };
    }
  }

  async fetchValidateTiersOctave(user: EdiOctaveUser): Promise<Response> {
    const accessToken = await this.getAccessToken();
    const apiFetchUrl = `${apiBaseUrl}/OAuthClient/RegisterAccount/ValidateTiersOctave`;
    const jsonUser = JSON.stringify(user);

    this.logger.info('Fetching bilan document:', {
      url: apiFetchUrl,
      jsonUser,
    });

    const response = await fetch(apiFetchUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: jsonUser,
    });

    this.logger.info('📥 Response fetch:', {
      url: response.url,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
      body: jsonUser,
    });

    if (!response.ok) {
      const errorText = await response.text();
      this.logger.warn('Error details:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  }
}
